import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { PageContainer } from "../../components/page_container/PageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  addImageAsync,
  clearImage,
  convertImageAsync,
  ImageFormat,
  maxResolutionHeightOnChanged,
  maxResolutionWidthOnChanged,
  selectState,
  targetFormatChanged,
} from "./ImageConverterSlice";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { CustomButton } from "../../components/custom_button/CustomButton";
import FileSaveIcon from "@mui/icons-material/SaveOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { HomeCard } from "../../components/home_card/HomeCard";
import AndroidIosImageResizerImage from "../../assets/AndroidIosImageResizer.webp";
import FaviconGeneratorImage from "../../assets/FaviconGenerator.webp";
import CompressionImage from "../../assets/Compression.webp";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export function ImageConverter() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("imageConverter.title")}
      pageSeoTitle={t("imageConverter.seoTitle")}
      pageDescription={t("imageConverter.description")}
    >
      <Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
        >
          <Grid2 xs={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t("imageConverter.mainHeading")}
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <b style={{ color: "#FFD9A8" }}>{t("imageConverter.step1")}</b> {t("imageConverter.step1Description")}
              <br />
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUploadIcon sx={{ color: "#FFD9A8" }} />}
                onChange={(e: any) => {
                  dispatch(addImageAsync(e.target.files[0]));
                }}
              >
                <Typography>{t("imageConverter.selectButton")}</Typography>
                <VisuallyHiddenInput
                  type="file"
                  accept="image/png, image/jpeg, image/webp, image/avif"
                />
              </Button>
            </Stack>
          </Grid2>
          <Grid2
            xs={12}
            sx={{
              display: state.selectedImage !== undefined ? "flex" : "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconButton onClick={() => dispatch(clearImage())}>
                <DeleteIcon sx={{ color: "#FFD9A8" }} />
              </IconButton>

              <Typography>{state.selectedImageName}</Typography>
            </Box>
          </Grid2>
          <Grid2 xs={12}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <b style={{ color: "#FFD9A8" }}>{t("imageConverter.step2")}</b> {t("imageConverter.step2Description")}
              <br />
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <CustomSelect
              label={t("imageConverter.convertToLabel")}
              value={state.targetFormat}
              onChange={(e) => {
                dispatch(
                  targetFormatChanged(Number(e.target.value) as ImageFormat)
                );
              }}
            >
              <MenuItem value={ImageFormat.png}>PNG</MenuItem>
              <MenuItem value={ImageFormat.jpg}>JPG</MenuItem>
              <MenuItem value={ImageFormat.webp}>WEBP</MenuItem>
              <MenuItem value={ImageFormat.avif}>AVIF</MenuItem>
            </CustomSelect>
          </Grid2>
          <Grid2
            xs={12}
            sx={{
              display: state.selectedImage != undefined ? "inline" : "none",
            }}
          >
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <b style={{ color: "#FFD9A8" }}>{t("imageConverter.step2_1")}</b> {t("imageConverter.step2_1Description")}
              <br />
            </Typography>
          </Grid2>
          <Grid2
            xs={6}
            sx={{
              display: state.selectedImage != undefined ? "inline" : "none",
            }}
          >
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <TextField
                label={t("imageConverter.maxResolutionHeight")}
                variant="outlined"
                color="info"
                value={state.maxResolutionHeight}
                onChange={(e) => {
                  dispatch(maxResolutionHeightOnChanged(e.target.value));
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography>{t("imageConverter.pixels")}</Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Grid2>
          <Grid2
            xs={6}
            sx={{
              display: state.selectedImage != undefined ? "inline" : "none",
            }}
          >
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <TextField
                label={t("imageConverter.maxResolutionWidth")}
                variant="outlined"
                color="info"
                value={state.maxResolutionWidth}
                onChange={(e) => {
                  dispatch(maxResolutionWidthOnChanged(e.target.value));
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography>{t("imageConverter.pixels")}</Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Grid2>
          <Grid2 xs={12}>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <b style={{ color: "#FFD9A8" }}>{t("imageConverter.step3")}</b> {t("imageConverter.step3Description")}
              <br />
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Box sx={{ mt: 1 }}>
                <CustomButton
                  icon={<FileSaveIcon sx={{ color: "#FFD9A8" }} />}
                  disabled={state.selectedImage === undefined}
                  label={t("imageConverter.convertButton")}
                  width={180}
                  onClick={() => {
                    dispatch(convertImageAsync());
                  }}
                />
              </Box>
            </Stack>
          </Grid2>
          <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
            <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
          </Grid2>
          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("imageConverter.content.modernFormatsHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("imageConverter.content.modernFormatsDescription")}
            </Typography>
            <AdSense
              adSlot={ADSENSE_SLOTS.IN_ARTICLE}
              adFormat="fluid"
              adLayoutKey="-fb+5w+4e-db+86"
              sx={{ mb: 3 }}
            />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("imageConverter.content.privacyHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("imageConverter.content.privacyDescription")}
            </Typography>
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("imageConverter.content.howToHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("imageConverter.content.howToDescription")}
            </Typography>
          </Grid2>
          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("imageConverter.related")}
            </Typography>
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="faviconGenerator"
              link="/FaviconGenerator"
              smallCard={true}
              image={FaviconGeneratorImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="androidIosResizer"
              link="/AndroidIosImageResizer"
              smallCard={true}
              image={AndroidIosImageResizerImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="compression"
              link="/Compression"
              smallCard={true}
              image={CompressionImage}
            />
          </Grid2>
        </Grid2>
      </Box>
    </SideMenuPageContainer>
  );
}
